/**
 * pushNotifications.js — minimal foundation helper for Wassla Web Push.
 *
 * Scope (foundation only):
 *   - Detect browser support
 *   - Register the /sw.js service worker
 *   - Ask for permission ONLY when called explicitly from a meaningful
 *     user gesture (saving an ad, opening notification settings).
 *   - Subscribe / unsubscribe via PushManager + POST to backend.
 *
 * Out of scope (deferred):
 *   - Sending real pushes server-side (pywebpush)
 *   - Admin test send
 *   - Category / location targeted pushes
 *
 * Strict isolation: this file does NOT touch the in-app bell (Header.js),
 * email pipeline, saved-search alerts, or notification preferences storage.
 * It only manages the browser PushSubscription lifecycle.
 */

import { Capacitor } from '@capacitor/core';

const BACKEND = process.env.REACT_APP_BACKEND_URL || '';
const PROMPT_KEY_PREFIX = 'wassla.push.prompted.v1.';

// ---------- Capability ----------

export function isPushSupported() {
  // Native Android v1.0.0 ships without push — hide all push UI there.
  if (Capacitor.isNativePlatform()) return false;
  return (
    typeof window !== 'undefined' &&
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window
  );
}

export function getCurrentPermission() {
  if (typeof Notification === 'undefined') return 'unsupported';
  return Notification.permission; // 'default' | 'granted' | 'denied'
}

// ---------- Service Worker ----------

let _swRegistrationPromise = null;

export function ensureServiceWorker() {
  if (!isPushSupported()) return Promise.resolve(null);
  if (_swRegistrationPromise) return _swRegistrationPromise;
  _swRegistrationPromise = navigator.serviceWorker
    .register('/sw.js', { scope: '/' })
    .catch((err) => {
      // Service worker registration can fail in private/incognito modes
      // or when the host blocks it. We swallow the error — the rest of the
      // app must keep working.
      // eslint-disable-next-line no-console
      console.warn('[push] sw register failed', err);
      _swRegistrationPromise = null;
      return null;
    });
  return _swRegistrationPromise;
}

// ---------- Helpers ----------

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

async function fetchVapidPublicKey() {
  const r = await fetch(`${BACKEND}/api/push/vapid-public-key`);
  if (!r.ok) throw new Error(`vapid key fetch failed (${r.status})`);
  const { public_key } = await r.json();
  return public_key;
}

function getAuthHeaders(token) {
  const h = { 'Content-Type': 'application/json' };
  if (token) h.Authorization = `Bearer ${token}`;
  return h;
}

// ---------- Prompt one-shot guard ----------

function promptKey(userId) {
  return `${PROMPT_KEY_PREFIX}${userId || 'anon'}`;
}

export function hasUserBeenPrompted(userId) {
  try { return localStorage.getItem(promptKey(userId)) === '1'; } catch { return false; }
}

export function markUserPrompted(userId) {
  try { localStorage.setItem(promptKey(userId), '1'); } catch { /* ignore */ }
}

// ---------- Public API ----------

/**
 * Subscribe the current user to push and POST the subscription to backend.
 * Caller MUST already have user's permission (call requestPermission first
 * via promptForPushIfMeaningful — never auto-prompt).
 */
export async function subscribeToPush({ token, language } = {}) {
  if (!isPushSupported()) return { ok: false, reason: 'unsupported' };
  if (Notification.permission !== 'granted') return { ok: false, reason: 'no-permission' };

  const reg = await ensureServiceWorker();
  if (!reg) return { ok: false, reason: 'no-sw' };

  let publicKey;
  try {
    publicKey = await fetchVapidPublicKey();
  } catch (e) {
    return { ok: false, reason: 'no-vapid', error: e };
  }

  let subscription = await reg.pushManager.getSubscription();
  if (!subscription) {
    try {
      subscription = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(publicKey),
      });
    } catch (e) {
      return { ok: false, reason: 'subscribe-failed', error: e };
    }
  }

  const body = {
    ...subscription.toJSON(),
    platform: 'web',
    language: language || (document.documentElement.lang === 'ar' ? 'ar' : 'en'),
  };

  try {
    const r = await fetch(`${BACKEND}/api/push/subscribe`, {
      method: 'POST',
      headers: getAuthHeaders(token),
      body: JSON.stringify(body),
    });
    if (!r.ok) return { ok: false, reason: `backend-${r.status}` };
    const data = await r.json();
    return { ok: true, subscription_id: data.subscription_id };
  } catch (e) {
    return { ok: false, reason: 'network', error: e };
  }
}

/**
 * Unsubscribe locally + tell the backend. Tolerates partial failures so the
 * UI never shows a stuck state.
 */
export async function unsubscribeFromPush({ token } = {}) {
  if (!isPushSupported()) return { ok: false, reason: 'unsupported' };
  let endpoint = null;
  try {
    const reg = await ensureServiceWorker();
    if (reg) {
      const sub = await reg.pushManager.getSubscription();
      if (sub) {
        endpoint = sub.endpoint;
        try { await sub.unsubscribe(); } catch { /* ignore */ }
      }
    }
  } catch { /* ignore */ }

  try {
    await fetch(`${BACKEND}/api/push/unsubscribe`, {
      method: 'POST',
      headers: getAuthHeaders(token),
      body: JSON.stringify(endpoint ? { endpoint } : {}),
    });
  } catch { /* ignore — local unsubscribe already done */ }
  return { ok: true };
}

/**
 * The single supported way to ASK for permission. Must be triggered by a
 * meaningful user gesture (saving an ad, opening notification settings).
 *
 * Idempotent + one-shot guarded by localStorage so we never re-prompt.
 *
 * Returns:
 *   { skipped: true }              when not supported, already prompted, or
 *                                  already granted/denied
 *   { granted: true,  subscribed } after permission accepted + subscribe call
 *   { granted: false }             when user dismissed / denied this time
 */
export async function promptForPushIfMeaningful({ userId, token, language } = {}) {
  if (!isPushSupported()) return { skipped: true, reason: 'unsupported' };

  // Hard guard — only ever ask the user once per device.
  if (hasUserBeenPrompted(userId)) return { skipped: true, reason: 'already-prompted' };

  const current = Notification.permission;
  if (current === 'denied') {
    markUserPrompted(userId); // never ask again on this device
    return { skipped: true, reason: 'denied' };
  }
  if (current === 'granted') {
    // Already granted at OS level — silently subscribe and remember.
    markUserPrompted(userId);
    const sub = await subscribeToPush({ token, language });
    return { granted: true, subscribed: sub };
  }

  // current === 'default' → safe to ask; this is gated by caller's gesture.
  let perm = 'default';
  try {
    perm = await Notification.requestPermission();
  } catch (e) {
    return { skipped: true, reason: 'request-failed', error: e };
  }
  markUserPrompted(userId);

  if (perm !== 'granted') return { granted: false };
  const sub = await subscribeToPush({ token, language });
  return { granted: true, subscribed: sub };
}
